import { useRef } from "react";
import { AvatarStack } from "@/components/ui/avatar-stack";
import { Skeleton } from "@/components/ui/skeleton";
import type { PublicStage, PublicTask } from "@/services/public-view.service";
import { PriorityBadge, TaskTypeChip } from "./PublicTaskBits";
import { groupByStage, publicAssignees } from "./utils";

function BoardCard({ task }: { task: PublicTask }) {
	const assignees = publicAssignees(task);

	return (
		<div className="flex flex-col gap-2 rounded-sm border border-border bg-surface p-3 shadow-xs">
			<div className="flex flex-wrap items-center gap-1.5">
				<TaskTypeChip type={task.task_type} />
				{/* Badge base forces text-[12px]!, so the override needs ! too. */}
				<PriorityBadge
					priority={task.priority}
					className="ml-auto px-1.5 py-0 text-[10px]! leading-4"
				/>
			</div>
			<div>
				<p className="text-[10px] text-muted">{task.project.name}</p>
				<p className="text-sm font-medium leading-snug text-foreground">
					{task.title}
				</p>
			</div>
			<div className="flex items-center justify-between gap-2 border-t border-border pt-2">
				{assignees.length > 0 ? (
					<div className="flex min-w-0 items-center gap-1.5">
						<AvatarStack profiles={assignees} size="sm" className="shrink-0" />
						<span className="truncate text-[11px] text-muted-foreground">
							{assignees.map((a) => a.full_name?.split(" ")[0] ?? "Unnamed").join(", ")}
						</span>
					</div>
				) : (
					<span className="text-[11px] text-muted">Unassigned</span>
				)}
				{task.sprint && (
					<span className="shrink-0 text-[11px] text-muted">
						{task.sprint.name}
					</span>
				)}
			</div>
		</div>
	);
}

export function PublicTaskBoard({
	tasks,
	stages,
	loading,
}: {
	tasks: PublicTask[];
	stages: PublicStage[];
	loading: boolean;
}) {
	const headerRef = useRef<HTMLDivElement>(null);

	if (loading) {
		return (
			<div className="flex gap-4 overflow-x-auto pb-4">
				{Array.from({ length: 4 }, (_, i) => (
					<Skeleton
						key={i}
						className="h-96 min-w-72 flex-1 rounded"
					/>
				))}
			</div>
		);
	}

	const columns = groupByStage(tasks, stages);

	return (
		<div>
			{/* Headers live outside the horizontal scroller so they can stick to the
			    page while it scrolls; their scrollLeft follows the columns below. */}
			<div
				ref={headerRef}
				className="sticky top-0 z-10 flex gap-4 overflow-hidden bg-background py-3"
			>
				{columns.map(({ stage, tasks: columnTasks }) => (
					<div
						key={stage.key}
						className="flex min-w-72 flex-1 items-center gap-2 px-1"
					>
						<span
							className="h-2 w-2 rounded-full"
							style={{ backgroundColor: stage.color }}
						/>
						<span className="text-sm font-semibold text-foreground">
							{stage.name}
						</span>
						<span className="ml-auto rounded-full bg-muted-subtle px-2 py-0.5 text-xs font-medium text-muted">
							{columnTasks.length}
						</span>
					</div>
				))}
			</div>

			<div
				className="flex gap-4 overflow-x-auto pb-4 content-stretch"
				onScroll={(e) => {
					if (headerRef.current)
						headerRef.current.scrollLeft =
							e.currentTarget.scrollLeft;
				}}
			>
				{columns.map(({ stage, tasks: columnTasks }) => (
					<section
						key={stage.key}
						aria-label={stage.name}
						className="flex min-w-72 flex-1 flex-col gap-2.5 rounded bg-muted-subtle p-2"
					>
						{columnTasks.map((task) => (
							<BoardCard key={task.id} task={task} />
						))}
					</section>
				))}
			</div>
		</div>
	);
}
